import React, { useState } from "react";
import { Box, LoadingOverlay } from "@mantine/core";
import { useTreeExpansion } from "../../common/GroupedTreeTable";
import ReportHeader from "../shell/ReportHeader";
import { ReportCanvasViewProps } from "./canvasTypes";
import CustomizeDrawer from "./customize/CustomizeDrawer";
import ReportActionRow from "./ReportActionRow";
import ReportHeaderActions from "./ReportHeaderActions";
import ReportKpiStrip from "./ReportKpiStrip";
import ReportToolbar from "./ReportToolbar";
import ReportTreeTable from "./ReportTreeTable";
import RunErrorPanel from "./RunErrorPanel";
import styles from "./ReportCanvas.module.css";

/**
 * The desktop layout of a v2 report: header with Save view / Discard,
 * toolbar (date, filters, Customize), KPI totals, action row and the grouped
 * table, with the Customize panel docked to the right when open (the report
 * narrows beside it; the table scrolls in both directions, so nothing is
 * hidden). Renders the model ReportCanvas owns; holds only layout state (the
 * Customize panel, expanded groups).
 */
export default function DesktopReportCanvas({
  canvas,
  definition,
  header,
  onDuplicate,
}: ReportCanvasViewProps) {
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const { draft, view, run, data, settled, table } = canvas;
  const expansion = useTreeExpansion(table.nodes);

  return (
    <Box className={styles.split}>
      <Box className={styles.page}>
        <ReportHeader
          {...header}
          duplicateLabel="Duplicate with this view"
          onDuplicate={onDuplicate}
          actions={
            <ReportHeaderActions
              dirty={draft.dirty}
              saving={draft.saving}
              onSave={() => void draft.save()}
              onDiscard={draft.discard}
              generatedAt={data?.generated_at}
              timezone={data?.timezone}
            />
          }
        />
        <Box className={styles.canvas}>
          <ReportToolbar
            definition={definition}
            view={view}
            facets={run.data?.facets}
            resolvedRange={run.data?.date_range}
            customizeOpen={customizeOpen}
            onUpdate={draft.update}
            onToggleCustomize={() => setCustomizeOpen((o) => !o)}
          />
          <ReportKpiStrip
            kpis={view.kpis}
            metricsByKey={canvas.byKey}
            totals={data?.totals.values ?? null}
            index={table.index}
            loading={run.loading}
          />
          <ReportActionRow
            lineCount={data?.totals.line_count ?? null}
            loading={run.loading}
            expansion={expansion}
            onPrint={canvas.printer.print}
            onExport={canvas.exportCsv}
            disabled={!settled}
          />
          <Box className={styles.tableArea} data-empty={!run.data || undefined}>
            <LoadingOverlay visible={run.loading} zIndex={4} />
            {run.error ? (
              <RunErrorPanel error={run.error} onRetry={run.retry} />
            ) : (
              run.data && (
                <ReportTreeTable
                  ariaLabel={header.name}
                  table={table}
                  expansion={expansion}
                  sort={view.sort}
                  onSortChange={(sort) => draft.update({ sort })}
                />
              )
            )}
          </Box>
        </Box>
      </Box>
      <CustomizeDrawer
        opened={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        definition={definition}
        catalog={canvas.catalog}
        view={view}
        onUpdate={draft.update}
      />
    </Box>
  );
}
